import React, { useEffect, useRef, useState, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card } from './ui/card';
import { Send, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ActivityChat — moment-based coordination.
 * Participants + host only. Post while 'open'; read-only after activity ends;
 * closed (empty) once the tail expires. No threads, no reactions, no feeds.
 */
export function ActivityChat({ activityId, isParticipant, isHost }) {
  const { user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [state, setState] = useState('open');
  const [canPost, setCanPost] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accessible, setAccessible] = useState(true);
  const pollRef = useRef(null);

  const authHeaders = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/activities/${activityId}/chat`, {
        headers: authHeaders(),
        withCredentials: true,
      });
      setMessages(res.data?.messages || []);
      setState(res.data?.state || 'closed');
      setCanPost(!!res.data?.can_post);
      setAccessible(true);
    } catch (err) {
      if (err?.response?.status === 403 || err?.response?.status === 401) setAccessible(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isParticipant && !isHost) { setLoading(false); setAccessible(false); return; }
    load();
    // Light polling while active — no websockets, no persistent connection.
    pollRef.current = setInterval(load, 20_000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId, isParticipant, isHost]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/api/activities/${activityId}/chat`,
        { message: text },
        { headers: authHeaders(), withCredentials: true },
      );
      setMessages(prev => [...prev, res.data?.message].filter(Boolean));
      setInput('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  const del = async (msg) => {
    try {
      await axios.delete(
        `${API}/api/activities/${activityId}/chat/${msg.message_id}`,
        { headers: authHeaders(), withCredentials: true },
      );
      setMessages(prev => prev.filter(m => m.message_id !== msg.message_id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete');
    }
  };

  if (loading || !accessible) return null;
  if (state === 'closed') return null;

  return (
    <Card
      className="p-4"
      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
      data-testid="activity-chat"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
            Coordination
          </h3>
          <p className="text-[11px]" style={{ color: 'var(--fy-text-secondary)' }}>
            {state === 'open'
              ? 'Quick notes to the group. Fades after the activity.'
              : 'Chat is read-only — the activity has ended.'}
          </p>
        </div>
      </div>

      <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="chat-messages">
        {messages.length === 0 ? (
          <p className="text-xs italic" style={{ color: 'var(--fy-text-disabled)' }}>
            No notes yet.
          </p>
        ) : (
          messages.map(m => {
            const mine = m.user_id === user?.user_id;
            const when = new Date(m.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
            return (
              <div
                key={m.message_id}
                className="flex gap-2 items-start group"
                data-testid={`chat-msg-${m.message_id}`}
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                  style={{
                    backgroundColor: m.is_host ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                    color: m.is_host ? '#fff' : 'var(--fy-text-secondary)',
                  }}
                >
                  {(m.user_name || 'U').slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <p className="text-xs font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                      {m.user_name}{m.is_host && <span className="ml-1 text-[10px] font-normal" style={{ color: 'var(--lk-sage)' }}>· host</span>}
                    </p>
                    <p className="text-[10px]" style={{ color: 'var(--fy-text-disabled)' }}>{when}</p>
                  </div>
                  <p className="text-sm whitespace-pre-wrap break-words" style={{ color: 'var(--fy-text-primary)' }}>
                    {m.message}
                  </p>
                </div>
                {(mine || isHost) && (
                  <button
                    type="button"
                    onClick={() => del(m)}
                    className="opacity-0 group-hover:opacity-100 p-1 transition-opacity"
                    aria-label="Delete message"
                    data-testid={`chat-delete-${m.message_id}`}
                  >
                    <Trash2 className="w-3 h-3" style={{ color: 'var(--fy-text-disabled)' }} />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {canPost && (
        <form onSubmit={send} className="mt-3 flex gap-2 items-end">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value.slice(0, 400))}
            placeholder="Running 5 min late…"
            rows={1}
            className="flex-1 px-3 py-2 rounded-xl text-sm outline-none resize-none"
            style={{
              backgroundColor: 'var(--fy-surface-muted)',
              border: '1px solid var(--fy-border-subtle)',
              color: 'var(--fy-text-primary)',
            }}
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="h-9 w-9 flex items-center justify-center rounded-full disabled:opacity-40"
            style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
            aria-label="Send"
            data-testid="chat-send"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      )}
    </Card>
  );
}
